import { FC } from 'react';
import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Divider, Stack } from '@mui/material';
import { APP, ROUTES } from 'config';
import NumberRangeFilter from 'components/filters/NumberRangeFilter';
import {
  AdminSuppliersFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import moment from 'moment';
import { formatCountry, formatList } from 'utils/format';
import { shopItemCategories } from 'types/MarketplaceItems';
import { DownloadXlsAction } from 'components/actions';
import { discountPackages, discountStatuses } from 'store/discounts';
import { useSearchParams } from 'utils/navigation';
import { useHistory } from 'react-router-dom';
import {
  COLOR_BY_PUBLISH_STATUS,
  COLOR_BY_STATUS,
  adminDiscountsFiltersSchema,
  discountPublishStatuses,
  useAdminDiscounts,
  useExportToXls,
} from 'store/admin-discounts';
import { DiscountActions } from '../atoms';

export const AdminDiscountsList: FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const { searchParams: filters, navigate } = useSearchParams(
    adminDiscountsFiltersSchema,
  );

  const { data: discounts, isLoading: isDiscountsLoading } =
    useAdminDiscounts(filters);

  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
          >
            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('discounts.actions.create'),
                  color: 'primary',
                  onClick: () => history.push(ROUTES.admin.discounts.create),
                },
              ]}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={discounts?.total} />
            <Stack direction="row" alignItems="center" gap={1}>
              <Filters
                value={filters}
                initialValues={adminDiscountsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('discounts.package.label')}
                      value={value.package}
                      initialValue={initialValues.package}
                      onChange={(v) => setFilter('package', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="package"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          searchable={false}
                          options={discountPackages.map((pack) => ({
                            key: pack,
                            label: t(`discounts.package.${pack}`),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('marketplaceItems.supplier')}
                      value={value.suppliers}
                      initialValue={initialValues.suppliers}
                      onChange={(v) => setFilter('suppliers', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <AdminSuppliersFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t(
                        'recognition.allowanceEmployeeOverviewListValidFrom',
                      )}
                      value={value.validAt}
                      initialValue={initialValues.validAt}
                      onChange={(v) => setFilter('validAt', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <DateRangeFilter
                          value={value}
                          onApply={applyFilter}
                          onClear={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('benefits.country')}
                      value={value.countries}
                      initialValue={initialValues.countries}
                      onChange={(v) => setFilter('countries', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="countries"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={APP.countries.alpha3.map((alpha3) => ({
                            key: alpha3,
                            label: formatCountry(i18n.language, alpha3),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('benefits.benefit_type')}
                      value={value.categories}
                      initialValue={initialValues.categories}
                      onChange={(v) => setFilter('categories', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="categories"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={shopItemCategories.map((category) => ({
                            key: category,
                            label: t(
                              `marketplaceItems.itemCategory-${category}`,
                            ),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('common.status')}
                      value={value.status}
                      initialValue={initialValues.status}
                      onChange={(v) => setFilter('status', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="status"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          searchable={false}
                          options={discountStatuses.map((status) => ({
                            key: status,
                            label: t(`status.${status}`),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('benefits.publish_status')}
                      value={value.publishStatus}
                      initialValue={initialValues.publishStatus}
                      onChange={(v) => setFilter('publishStatus', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="publishStatus"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          searchable={false}
                          options={discountPublishStatuses.map(
                            (publishStatus) => ({
                              key: publishStatus,
                              label: t(`status.${publishStatus}`),
                            }),
                          )}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('news.seen')}
                      value={value.viewsCount}
                      initialValue={initialValues.viewsCount}
                      onChange={(v) => setFilter('viewsCount', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <NumberRangeFilter
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <OpenSearchFilter
                      value={value.search}
                      onApplyFilter={(v) => setFilter('search', v)}
                      placeholder={t('common.search')}
                    />
                  </>
                )}
              </Filters>

              <Divider orientation="vertical" variant="middle" flexItem />

              <DownloadXlsAction
                listLength={discounts?.total ?? 0}
                exportToExcel={() => exportToXls({ filters })}
                loading={isExportingToXls}
              />
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={discounts?.items ?? []}
        isLoading={isDiscountsLoading}
        columns={[
          {
            id: 'name',
            header: t('common.name'),
            cell: ({ row: { original: discount } }) => (
              <Link
                label={discount.name}
                to={ROUTES.admin.discounts.details.settings.replace(
                  ':id',
                  discount.id,
                )}
              />
            ),
          },
          {
            id: 'package',
            header: t('discounts.package.label'),
            cell: ({ row: { original: discount } }) =>
              formatList(
                i18n.language,
                discount.packages.map((pack) => t(`discounts.package.${pack}`)),
              ),
          },
          {
            id: 'supplier',
            header: t('marketplaceItems.supplier'),
            cell: ({ row: { original: discount } }) =>
              discount.supplier ? (
                <Link
                  label={discount.supplier.name}
                  to={ROUTES.admin.suppliers.details.replace(
                    ':id',
                    discount.supplier.id,
                  )}
                />
              ) : null,
          },
          {
            id: 'priority',
            header: t('common.priority.label'),
            cell: ({ row: { original: discount } }) => discount.priority,
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'validTo',
            header: t('recognition.allowanceEmployeeOverviewListValidTo'),
            cell: ({ row: { original: discount } }) =>
              discount.validTo ? (
                <Typography
                  color={
                    moment(discount.validTo).isBefore()
                      ? 'error'
                      : 'textPrimary'
                  }
                >
                  {moment(discount.validTo).format('l')}
                </Typography>
              ) : null,
            meta: { sort: true },
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: discount } }) => (
              <Typography color={COLOR_BY_STATUS[discount.status]}>
                {t(`status.${discount.status}`)}
              </Typography>
            ),
          },
          {
            id: 'publishStatus',
            header: t('benefits.publish_status'),
            cell: ({ row: { original: discount } }) => (
              <Typography
                color={COLOR_BY_PUBLISH_STATUS[discount.publishStatus]}
              >
                {t(`status.${discount.publishStatus}`)}
              </Typography>
            ),
          },
          {
            id: 'favoritesCount',
            header: t('discounts.favorites.label'),
            cell: ({ row: { original: discount } }) => discount.favoritesCount,
            meta: { align: 'right' },
          },
          {
            id: 'viewsCount',
            header: t('news.seen'),
            cell: ({ row: { original: discount } }) => discount.viewsCount,
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'uniqueViewsCount',
            header: t('discounts.uniqueViewsCount.label'),
            cell: ({ row: { original: discount } }) =>
              discount.uniqueViewsCount,
            meta: { align: 'right' },
          },
          {
            id: 'actions',
            cell: ({ row: { original: discount } }) => {
              return (
                <DiscountActions
                  type="compact"
                  id={discount.id}
                  name={discount.name}
                />
              );
            },
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          discounts && {
            count: discounts.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
